/**
 * The one-time "switch any time, lose nothing" reminder (Tom, 2026-09-28).
 *
 * New learners start on Easy. Fairly early on, once, they are told they can
 * move between Easy and Fast whenever they like and lose nothing by it. The
 * line sits directly under the Easy / Fast switch on the resting screen, so it
 * points at the control by being next to it.
 *
 * WHEN: the first time the resting screen shows after the learner has finished
 * a round, a LEGO's worth of practice, in this sitting. The resting screen is
 * only ever up while practice is stopped, so the reminder can never interrupt
 * learning (HARD RULE ssi-nothing-interrupts-learning). It is marked seen when
 * that resting visit ends, so it is shown on exactly one visit and never again
 * on this device.
 *
 * WHO: early learners only. The upper bound keeps the reminder off the screens
 * of people deep into a course, who met the switch long ago, and off class
 * accounts, where the mode belongs to the teacher driving the lesson.
 *
 * Why a round finished in this sitting rather than the course position: a new
 * learner's position already reads seed 1 before they have played a note (the
 * phone-width walk caught the line showing on a brand-new account's very first
 * screen), and a guest's finished rounds are not stored anywhere to read back.
 */

export const MODE_SWITCH_REMINDER_KEY = 'ssi-mode-switch-reminder-seen'

/** The reminder is for the start of a course: no later than this seed. */
export const MODE_SWITCH_REMINDER_LAST_SEED = 20

export interface ModeSwitchReminderInput {
  /** Already shown once on this device. */
  seen: boolean
  /** The player has its progress and is showing the Easy / Fast switch. */
  playerReady: boolean
  /** Rounds finished since the player opened. */
  roundsFinished: number
  /** Seed of the learner's position in the course. */
  courseSeed: number
  /** Playing as a class. */
  inClass: boolean
}

export function shouldShowModeSwitchReminder(input: ModeSwitchReminderInput): boolean {
  if (input.seen || input.inClass || !input.playerReady) return false
  return input.roundsFinished >= 1 && input.courseSeed <= MODE_SWITCH_REMINDER_LAST_SEED
}

export function readModeSwitchReminderSeen(): boolean {
  try { return localStorage.getItem(MODE_SWITCH_REMINDER_KEY) === '1' } catch { return false }
}

export function markModeSwitchReminderSeen(): void {
  try { localStorage.setItem(MODE_SWITCH_REMINDER_KEY, '1') } catch { /* storage blocked — it shows again next visit, harmlessly */ }
}
